import { readFileOrUrlWithCache, parseInterpolationStrings, getInterpolatedHeadersFactory, getInterpolatedStringFactory, getHeadersObject, loadFromModuleExportExpression, getCachedFetch, } from '@graphql-mesh/utils';
import { createGraphQLSchema, GraphQLOperationType } from './openapi-to-graphql';
import { set } from 'lodash';
export default class OpenAPIHandler {
    constructor({ config, baseDir, cache, pubsub, introspectionCache = {}, }) {
        this.config = config;
        this.baseDir = baseDir;
        this.cache = cache;
        this.pubsub = pubsub;
        this.introspectionCache = introspectionCache;
    }
    async getCachedSpec() {
        const { source } = this.config;
        if (!this.introspectionCache.spec) {
            this.introspectionCache.spec =
                typeof source !== 'string'
                    ? source
                    : await readFileOrUrlWithCache(source, this.cache, {
                        cwd: this.baseDir,
                        fallbackFormat: this.config.sourceFormat,
                        headers: this.config.schemaHeaders,
                    });
        }
        return this.introspectionCache.spec;
    }
    async getMeshSource() {
        var _a, _b, _c;
        const { addLimitArgument, baseUrl, customFetch, genericPayloadArgName, operationHeaders, qs, selectQueryOrMutationField, } = this.config;
        const spec = await this.getCachedSpec();
        let fetch;
        if (customFetch) {
            fetch = await loadFromModuleExportExpression(customFetch, { defaultExportName: 'default', cwd: this.baseDir });
        }
        else {
            fetch = getCachedFetch(this.cache);
        }
        const baseUrlFactory = getInterpolatedStringFactory(baseUrl);
        const headersFactory = getInterpolatedHeadersFactory(operationHeaders);
        const queryStringFactoryMap = new Map();
        for (const queryName in qs || {}) {
            queryStringFactoryMap.set(queryName, getInterpolatedStringFactory(qs[queryName]));
        }
        const searchParamsFactory = (resolverData, searchParams) => {
            for (const queryName in qs || {}) {
                searchParams.set(queryName, queryStringFactoryMap.get(queryName)(resolverData));
            }
            return searchParams;
        };
        const { schema } = await createGraphQLSchema(spec, {
            fetch,
            baseUrl: baseUrl,
            operationIdFieldNames: true,
            fillEmptyResponses: true,
            includeHttpDetails: this.config.includeHttpDetails,
            genericPayloadArgName: genericPayloadArgName === undefined ? false : genericPayloadArgName,
            selectQueryOrMutationField: selectQueryOrMutationField === undefined
                ? {}
                : selectQueryOrMutationField.reduce((acc, curr) => {
                    let operationType;
                    switch (curr.type) {
                        case 'Query':
                            operationType = GraphQLOperationType.Query;
                            break;
                        case 'Mutation':
                            operationType = GraphQLOperationType.Mutation;
                            break;
                    }
                    set(acc, `${curr.title}.${curr.path}.${curr.method}`, operationType);
                    return acc;
                }, {}),
            addLimitArgument: addLimitArgument === undefined ? true : addLimitArgument,
            sendOAuthTokenInQuery: true,
            viewer: false,
            equivalentToMessages: true,
            pubsub: this.pubsub,
            resolverMiddleware: (getResolverParams, originalFactory) => (root, args, context, info) => {
                const resolverData = { root, args, context, info };
                const resolverParams = getResolverParams();
                resolverParams.requestOptions = {
                    headers: getHeadersObject(headersFactory(resolverData)),
                };
                resolverParams.qs = qs;
                /* FIXME: baseUrl is coming from Fastify Request
                if (context?.baseUrl) {
                  resolverParams.baseUrl = context.baseUrl;
                }
                */
                if (!resolverParams.baseUrl && baseUrl) {
                    resolverParams.baseUrl = baseUrlFactory(resolverData);
                }
                if (resolverParams.baseUrl) {
                    const urlObj = new URL(resolverParams.baseUrl);
                    searchParamsFactory(resolverData, urlObj.searchParams);
                } /* else {
                  console.warn(
                    `There is no 'baseUrl' defined for this OpenAPI definition. We recommend you to define one manually!`
                  );
                } */
                if (context === null || context === void 0 ? void 0 : context.fetch) {
                    resolverParams.fetch = context.fetch;
                }
                if (context === null || context === void 0 ? void 0 : context.qs) {
                    resolverParams.qs = context.qs;
                }
                return originalFactory(() => resolverParams)(root, args, context, info);
            },
        });
        const { args, contextVariables } = parseInterpolationStrings(Object.values(operationHeaders || {}));
        const rootFields = [
            ...Object.values(((_a = schema.getQueryType()) === null || _a === void 0 ? void 0 : _a.getFields()) || {}),
            ...Object.values(((_b = schema.getMutationType()) === null || _b === void 0 ? void 0 : _b.getFields()) || {}),
            ...Object.values(((_c = schema.getSubscriptionType()) === null || _c === void 0 ? void 0 : _c.getFields()) || {}),
        ];
        for (const rootField of rootFields) {
            for (const argName in args) {
                const argConfig = args[argName];
                rootField.args.push({
                    name: argName,
                    description: undefined,
                    defaultValue: undefined,
                    extensions: undefined,
                    astNode: undefined,
                    deprecationReason: undefined,
                    ...argConfig,
                });
            }
        }
        contextVariables.push('fetch' /*, 'baseUrl' */);
        return {
            schema,
            contextVariables,
        };
    }
}
//# sourceMappingURL=index.js.map